import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, Snackbar } from '@mui/material';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Kicker, AmberButton, EngGrid } from '../components/design';

const MONO = "'IBM Plex Mono', monospace";
const PRODUCT_ID = process.env.REACT_APP_STRIPE_PRODUCT_ID;

const FEATURES = [
  { label: 'Single-page P&ID analysis',    tag: 'DRAWING' },
  { label: 'Text & object identification',  tag: 'DETECT' },
  { label: 'Connection analysis',           tag: 'GRAPH' },
  { label: 'Interactive visualization',     tag: 'VIEW' },
  { label: 'Rapid verification workflow',   tag: 'VERIFY' },
  { label: 'Export to Excel & graph data',  tag: 'EXPORT' },
];

const TestCardInfo = () => {
  if (process.env.NODE_ENV === 'production') return null;
  return (
    <Box
      sx={{
        maxWidth: '900px',
        mx: 'auto',
        mb: '24px',
        p: '16px 20px',
        border: '1px dashed rgba(45,212,191,.4)',
        borderRadius: '10px',
        background: 'rgba(45,212,191,.05)',
        fontFamily: MONO,
        fontSize: '13px',
        color: '#2dd4bf',
      }}
    >
      Test mode — card: <strong>4242 4242 4242 4242</strong> &nbsp;Exp: 12/25 &nbsp;CVC: 123 &nbsp;ZIP: 12345
    </Box>
  );
};

const PricingPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openAlert, setOpenAlert] = useState(!!location.state?.message);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useContext(AuthContext);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const fetchProductInfo = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/payments/products/${PRODUCT_ID}`);
        setProduct(response.data);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchProductInfo();
  }, []);

  const handleSelectPlan = () => {
    if (!product) return;
    const planData = {
      planName: product.name,
      price: product.defaultPrice.unitAmount,
      priceId: product.defaultPrice.id,
      description: product.description,
      recurringInterval: product.defaultPrice.recurringInterval,
      recurringIntervalCount: product.defaultPrice.recurringIntervalCount,
      id: product.id,
      from: '/pricing',
    };
    if (user) {
      navigate('/checkout', { state: planData });
    } else {
      navigate('/register', { state: { ...planData, redirectToCheckout: true } });
    }
  };

  const formatPrice = (product) => {
    if (!product?.defaultPrice) return '$500';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: product.defaultPrice.currency || 'USD', minimumFractionDigits: 0 })
      .format(product.defaultPrice.unitAmount || 500);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', background: '#0a1326' }}>
          <CircularProgress sx={{ color: '#2dd4bf' }} />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <Box sx={{ maxWidth: '600px', mx: 'auto', mt: '60px', px: '28px', background: '#0a1326' }}>
          <Alert
            severity="error"
            sx={{ background: 'rgba(244,113,106,.1)', color: '#f4a39e', border: '1px solid rgba(244,113,106,.3)', mb: '16px' }}
          >
            {error}
          </Alert>
          <AmberButton onClick={() => window.location.reload()}>Try again</AmberButton>
        </Box>
      </>
    );
  }

  const priceDisplay = formatPrice(product);
  const billingLabel = product?.defaultPrice?.recurringInterval === 'year' ? '/ year' : '/ month';

  return (
    <Box sx={{ background: '#0a1326', minHeight: '100vh' }}>
      <Navbar />

      {/* Header */}
      <Box
        component="header"
        sx={{
          position: 'relative',
          background: 'radial-gradient(1000px 540px at 50% -10%, rgba(47,109,240,.18), transparent 60%), #0a1326',
          pt: '74px',
          pb: '18px',
          px: '28px',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <EngGrid opacity={0.5} />
        <Box sx={{ position: 'relative', maxWidth: '780px', mx: 'auto' }}>
          <Kicker variant="pill">Pricing</Kicker>
          <Box
            component="h1"
            sx={{
              m: 0,
              mb: '18px',
              fontSize: 'clamp(38px,5vw,58px)',
              lineHeight: 1.05,
              fontWeight: 700,
              letterSpacing: '-.02em',
              color: '#f4f8ff',
              textWrap: 'balance',
            }}
          >
            One price. Every feature.
          </Box>
          <Box sx={{ mx: 'auto', maxWidth: '540px', fontSize: '18px', lineHeight: 1.6, color: '#aebfdd' }}>
            One annual price for your user account — unlimited drawings and every feature PANDID offers, with no per-sheet fees.
          </Box>
        </Box>
      </Box>

      {/* Plan card section */}
      <Box
        component="section"
        sx={{ position: 'relative', background: '#0a1326', pt: '40px', pb: '64px', px: '28px', overflow: 'hidden' }}
      >
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(680px 360px at 50% 30%, rgba(45,212,191,.12), transparent 62%)' }} />
        <Box sx={{ position: 'relative', maxWidth: '900px', mx: 'auto' }}>
          <TestCardInfo />

          {/* The engineering datasheet card */}
          <Box
            sx={{
              border: '1px solid rgba(120,150,200,.28)',
              borderRadius: '18px',
              background: 'linear-gradient(180deg,#0e1c39,#0a1326)',
              boxShadow: '0 40px 90px rgba(0,0,0,.5)',
              overflow: 'hidden',
            }}
          >
            {/* Title-block header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: '20px',
                py: '13px',
                borderBottom: '1px solid rgba(120,150,200,.2)',
                background: 'rgba(8,15,30,.55)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontFamily: MONO, fontSize: '11.5px', letterSpacing: '.1em', color: '#90a6cc' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#2dd4bf', animation: 'pulseDot 1.8s infinite', '@keyframes pulseDot': { '0%,100%': { opacity: .35 }, '50%': { opacity: 1 } } }} />
                PLAN · ANNUAL — REV 2026
              </Box>
              <Box sx={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '.08em', color: '#06121f', background: '#2dd4bf', borderRadius: '999px', px: '11px', py: '5px' }}>
                ALL FEATURES INCLUDED
              </Box>
            </Box>

            {/* Body — price + features columns */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '.92fr 1.12fr' } }}>
              {/* Price column */}
              <Box sx={{ p: { xs: '28px 24px', sm: '38px 34px' } }}>
                <Box sx={{ fontFamily: MONO, fontSize: '12px', letterSpacing: '.14em', textTransform: 'uppercase', color: '#8298bd', mb: '16px' }}>
                  Annual subscription
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <Box sx={{ fontFamily: MONO, fontSize: 'clamp(44px,6vw,62px)', fontWeight: 600, color: '#f4f8ff', lineHeight: .9, letterSpacing: '-.02em' }}>
                    {priceDisplay}
                  </Box>
                  <Box sx={{ fontFamily: MONO, fontSize: '16px', color: '#8298bd', whiteSpace: 'nowrap' }}>
                    {billingLabel}
                  </Box>
                </Box>
                <Box sx={{ fontFamily: MONO, fontSize: '12.5px', color: '#7f93b8', mt: '14px', letterSpacing: '.04em' }}>
                  billed annually · one user · unlimited drawings
                </Box>

                <AmberButton
                  onClick={handleSelectPlan}
                  size="lg"
                  fullWidth
                  sx={{ mt: '30px', fontSize: '16px', py: '15px' }}
                >
                  Subscribe now <Box component="span" sx={{ fontFamily: MONO }}>→</Box>
                </AmberButton>
              </Box>

              {/* Features column */}
              <Box
                sx={{
                  p: { xs: '24px', sm: '34px' },
                  borderLeft: { xs: 'none', sm: '1px solid rgba(120,150,200,.2)' },
                  borderTop: { xs: '1px solid rgba(120,150,200,.2)', sm: 'none' },
                }}
              >
                <Box sx={{ fontFamily: MONO, fontSize: '12px', letterSpacing: '.14em', textTransform: 'uppercase', color: '#2dd4bf', mb: '20px' }}>
                  {`// what's included`}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {FEATURES.map(({ label, tag }) => (
                    <Box key={tag} sx={{ display: 'flex', alignItems: 'flex-start', gap: '13px' }}>
                      <Box
                        sx={{
                          mt: '2px',
                          width: 19,
                          height: 19,
                          borderRadius: '5px',
                          border: '1.5px solid #2dd4bf',
                          color: '#2dd4bf',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          flexShrink: 0,
                        }}
                      >
                        ✓
                      </Box>
                      <Box>
                        <Box sx={{ fontSize: '15.5px', color: '#eaf0fb' }}>{label}</Box>
                        <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#6f86ad', letterSpacing: '.08em', mt: '2px' }}>{tag}</Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Datasheet footer tick */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: '20px',
                py: '11px',
                borderTop: '1px solid rgba(120,150,200,.16)',
                background: 'rgba(8,15,30,.4)',
                fontFamily: MONO,
                fontSize: '10.5px',
                letterSpacing: '.1em',
                color: '#4f648a',
              }}
            >
              <span>PANDID-PLAN-01</span>
              <span>SHT 1 OF 1</span>
            </Box>
          </Box>

          {/* Reassurance chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', mt: '26px' }}>
            {['One user account', 'Unlimited drawings', 'No per-sheet fees'].map((label) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  border: '1px solid rgba(120,150,200,.22)',
                  borderRadius: '999px',
                  px: '16px',
                  py: '9px',
                  fontFamily: MONO,
                  fontSize: '12px',
                  color: '#aebfdd',
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#2dd4bf', flexShrink: 0 }} />
                {label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Snackbar open={openAlert} autoHideDuration={6000} onClose={() => setOpenAlert(false)}>
        <Alert onClose={() => setOpenAlert(false)} severity="info" sx={{ width: '100%' }}>
          {location.state?.message}
        </Alert>
      </Snackbar>

      <Footer />
    </Box>
  );
};

export default PricingPlans;
